<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { AppAlert, DialogPanel, FormInput, useDialog, useToast } from 'duanaga-ui'
import { z } from 'zod'
import FetchError from '@/FetchError'
import CodeApi from '@/api/Code'
import DialogAction from '@/components/DialogAction.vue'
import DialogHeader from '@/components/DialogHeader.vue'
import { useValidator } from '@/composables/validator'
import useBatchStore from '@/stores/batch'
import useCodeStore from '@/stores/code'

const { hideDialog } = useDialog()
const { toastSuccess, toastError } = useToast()
const batchStore = useBatchStore()
const codeStore = useCodeStore()
const { selected } = storeToRefs(batchStore)

const schema = z.object({
  quantity: z
    .number()
    .min(1, 'Quantity must be at least 1')
    .max(10000, 'Maximum 10,000 codes allowed')
})

const { formData, formError, validate } = useValidator(schema, {
  quantity: 100
})

const submitting = ref(false)
const clientError = ref<string | null>(null)
const title = computed(() => 'Generate Codes')
const subtitle = computed(() =>
  selected.value
    ? `Generate unique codes for batch ${selected.value.number}`
    : 'Select a batch to begin.'
)

const handleSubmit = async (): Promise<void> => {
  if (!selected.value || !validate()) {
    clientError.value = selected.value ? 'Please fix the errors above.' : 'No batch selected.'
    return
  }

  submitting.value = true
  clientError.value = null

  try {
    await CodeApi.generateCodes(selected.value.id, formData.value.quantity)
    toastSuccess(`Successfully generated ${formData.value.quantity} codes`)

    // Refetch both code and batch data after successful generation
    codeStore.setRefetch(true)
    batchStore.setRefetch(true)

    hideDialog()
  } catch (error) {
    const fetchError = error as FetchError
    clientError.value = fetchError.message || 'Unable to generate codes.'
    toastError(clientError.value)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <DialogPanel class="w-full md:w-[480px]">
    <DialogHeader
      :title="title"
      :subtitle="subtitle"
      closable
    />

    <form @submit.prevent="handleSubmit">
      <div class="px-24 pt-12 md:px-32">
        <AppAlert
          v-if="clientError"
          type="error"
          :message="clientError"
          show
          @dismiss-alert="clientError = null"
        />
      </div>

      <div class="flex flex-col gap-24 px-24 pb-24 pt-12 md:px-32">
        <div class="gap-24 rounded-8 border border-grey-30 bg-grey-20/60 p-24">
          <div class="space-y-8">
            <FormInput
              v-model.number="formData.quantity"
              type="number"
              label="Number of codes to generate"
              placeholder="100"
              :error="formError.quantity"
              required
            />
          </div>
        </div>
      </div>

      <DialogAction :submitting="submitting" />
    </form>
  </DialogPanel>
</template>
